import { getPerformanceCalculatorChoices, translateCalculators } from 'constants/calculators'
import { getWeatherDatasetChoices, translateWeatherDatasets } from 'constants/weatherDatasets'
import { authSelectors } from 'ducks/auth'
import CustomField from 'elements/field/CustomField'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import { useExcludedWeatherDatasets } from 'hooks/useExcludedWeatherDatasets'
import React, { useMemo } from 'react'
import { SelectInput, useTranslate } from 'react-admin'
import { useFormState } from 'react-final-form'
import { useSelector } from 'react-redux'

const PERFORMANCE_CALCULATOR_TOOLTIP_KEY =
  'Auto determines the latest appropriate calculator based on project location (recommended). Choosing a specific calculator (e.g. SAM, PVWatts) uses that engine for all energy production calculations on designs using this configuration.'

function numericFormValue(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === '') return fallback
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

export type ProjectConfigurationEnergyFieldProps = {
  disabled: boolean
  allowEmpty?: boolean
}

export const PerformanceCalculatorFormRow: React.FC<ProjectConfigurationEnergyFieldProps> = ({
  disabled,
  allowEmpty = false,
}) => {
  const translate = useTranslate()
  const formState = useFormState<{ performance_calculator?: unknown }>()
  const orgCountry = useSelector(authSelectors.getCurrentOrgCountry) || ''
  const performanceCalculator = formState.values.performance_calculator
  const performanceCalculatorId = numericFormValue(performanceCalculator, 2)
  const performanceCalculatorChoices = useMemo(
    () => getPerformanceCalculatorChoices(performanceCalculatorId, orgCountry, [], true),
    [performanceCalculatorId, orgCountry]
  )
  const choices = useMemo(
    () =>
      translateCalculators(performanceCalculatorChoices, translate).map((pc) => ({
        id: pc.id,
        name: pc.name,
      })),
    [performanceCalculatorChoices, translate]
  )

  const selectProps = {
    component: SelectInput,
    source: 'performance_calculator',
    name: 'performance_calculator',
    label: 'Energy Production Calculator',
    choices,
    translateChoice: false,
    disabled,
    style: { marginRight: 0 },
    ...(allowEmpty
      ? {
          allowEmpty: true,
          fullWidth: true,
          emptyText: translate('ra.action.clear_input_value'),
        }
      : {}),
  } as const

  const tooltip = (
    <InfoTooltip placement="right" title={translate(PERFORMANCE_CALCULATOR_TOOLTIP_KEY)} severity="info" size="small" />
  )

  if (allowEmpty) {
    return (
      <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start', columnGap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <CustomField {...selectProps} />
        </div>
        {tooltip}
      </div>
    )
  }

  return (
    <span style={{ display: 'inline-flex', marginRight: 30 }}>
      <CustomField {...selectProps} />
      {tooltip}
    </span>
  )
}

export const WeatherDatasetFormRow: React.FC<ProjectConfigurationEnergyFieldProps> = ({
  disabled,
  allowEmpty = false,
}) => {
  const translate = useTranslate()
  const formState = useFormState<{ weather_dataset?: unknown }>()
  const orgCountry = useSelector(authSelectors.getCurrentOrgCountry) || ''
  const filteredWeatherDatasets = useExcludedWeatherDatasets()
  const weatherDataset = formState.values.weather_dataset
  const weatherDatasetId = numericFormValue(weatherDataset, 0)
  const weatherDatasetChoices = useMemo(
    () => getWeatherDatasetChoices(weatherDatasetId, orgCountry, filteredWeatherDatasets, true),
    [weatherDatasetId, orgCountry, filteredWeatherDatasets]
  )
  const weatherDatasetSelectChoices = useMemo(
    () => translateWeatherDatasets(weatherDatasetChoices, translate).map((wd) => ({ id: wd.id, name: wd.name })),
    [weatherDatasetChoices, translate]
  )

  const field = (
    <CustomField
      component={SelectInput}
      source="weather_dataset"
      name="weather_dataset"
      label="Weather Dataset (SAM Only)"
      choices={weatherDatasetSelectChoices}
      translateChoice={false}
      disabled={disabled}
      {...(allowEmpty
        ? {
            allowEmpty: true,
            fullWidth: true,
            emptyText: translate('ra.action.clear_input_value'),
          }
        : {})}
    />
  )

  return allowEmpty ? <div style={{ width: '100%' }}>{field}</div> : field
}
